"use client";

import type { GovernedNonAnswerable } from "@mlx/contracts";
import { CircleHelp, Info, Loader2, Search } from "lucide-react";

import { Button } from "@/components/ui/button";

type CustomSqlExecutionCapability = {
  isPending?: boolean;
  onStart: () => void;
};

export function GovernedNonAnswerableCard(props: {
  outcome: GovernedNonAnswerable;
  customSqlExecution?: CustomSqlExecutionCapability;
}) {
  const needsClarification = props.outcome.kind === "needs_clarification";
  const title = needsClarification
    ? "I need one more detail"
    : "I couldn’t answer that from the available business data";
  const Icon = needsClarification ? CircleHelp : Info;

  return (
    <section
      className="max-w-[720px] border-l-2 border-amber-500/50 py-0.5 pl-3"
      data-governed-non-answerable={props.outcome.kind}
    >
      <div className="flex items-start gap-2">
        <Icon
          className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-400"
          aria-hidden
        />
        <div className="flex min-w-0 flex-col gap-2">
          <h3 className="text-sm font-medium text-foreground">{title}</h3>
          <p className="text-sm leading-relaxed text-foreground">
            {needsClarification
              ? props.outcome.suggestedFollowUp
              : props.outcome.explanation}
          </p>
          {!needsClarification &&
          props.outcome.suggestedFollowUp !== props.outcome.explanation ? (
            <p className="text-sm leading-relaxed text-muted-foreground">
              You could try: {props.outcome.suggestedFollowUp}
            </p>
          ) : null}
          {props.customSqlExecution ? (
            <div
              className="flex flex-col items-start gap-2 pt-1"
              onClick={(event) => event.stopPropagation()}
            >
              <p className="text-xs leading-relaxed text-muted-foreground">
                I can look more broadly with a read-only custom query.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                disabled={props.customSqlExecution.isPending}
                onClick={props.customSqlExecution.onStart}
              >
                {props.customSqlExecution.isPending ? (
                  <Loader2
                    data-icon="inline-start"
                    className="animate-spin motion-reduce:animate-none"
                    aria-hidden
                  />
                ) : (
                  <Search data-icon="inline-start" aria-hidden />
                )}
                {props.customSqlExecution.isPending
                  ? "Looking more broadly…"
                  : "Let the agent explore"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
